import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

export default function FAQs() {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', name: 'All Questions' },
    { id: 'general', name: 'General' },
    { id: 'providers', name: 'For Providers' },
    { id: 'users', name: 'For Users' },
    { id: 'pricing', name: 'Pricing & Plans' },
    { id: 'technical', name: 'Technical Support' },
  ];

  const faqs: FAQItem[] = [
    {
      category: 'general',
      question: 'What is Revulist and how does it work?',
      answer: 'Revulist is a comprehensive healthcare provider directory platform that connects patients with quality medical professionals in North Brunswick, New Jersey and surrounding areas. Our platform allows users to search for healthcare providers by specialty, location, insurance accepted, and patient reviews. Healthcare providers can create free listings to showcase their practice, credentials, and services.',
    },
    {
      category: 'general',
      question: 'Is Revulist free to use for patients?',
      answer: 'Yes, Revulist is completely free for patients and users searching for healthcare providers. You can browse listings, read reviews, compare providers, and contact practices at no cost. Our mission is to make quality healthcare accessible and transparent for everyone in the community.',
    },
    {
      category: 'general',
      question: 'How do I search for a healthcare provider on Revulist?',
      answer: 'You can search for providers using our advanced search filters on the homepage or vendors page. Filter by specialty (such as Primary Care, Cardiology, Dermatology), location, insurance accepted, languages spoken, and patient ratings. You can also view detailed provider profiles including credentials, services offered, office hours, and patient reviews.',
    },
    {
      category: 'providers',
      question: 'How can I list my medical practice on Revulist?',
      answer: 'Listing your practice on Revulist is simple and free! Click on "Register" or "Add Listing" in the navigation menu, create your provider account, and fill out your practice information including specialty, credentials, services, insurance accepted, and office details. Once submitted, your listing will be reviewed and published within 24-48 hours.',
    },
    {
      category: 'providers',
      question: 'What information should I include in my provider profile?',
      answer: 'A complete provider profile should include: your medical specialty and credentials (medical school, residency, board certifications), years of experience, services offered, insurance plans accepted, languages spoken, office location and hours, contact information, and a professional bio. Adding high-quality photos of your practice and staff can also help attract more patients.',
    },
    {
      category: 'providers',
      question: 'Can I claim an existing listing for my practice?',
      answer: 'Yes! If you find an existing listing for your practice that you did not create, you can claim it by clicking "Claim Your Listing" in the footer menu. You will need to verify your identity and ownership of the practice through our verification process. Once verified, you will gain full control to edit and manage your listing.',
    },
    {
      category: 'providers',
      question: 'How do I manage patient reviews on my listing?',
      answer: 'As a verified provider, you can manage reviews through your My Account dashboard. You can approve pending reviews, respond to patient feedback, and flag inappropriate content. We encourage providers to engage professionally with reviews as it demonstrates commitment to patient care and helps build trust with potential patients.',
    },
    {
      category: 'users',
      question: 'How can I leave a review for a healthcare provider?',
      answer: 'To leave a review, visit the provider\'s profile page and click the "Write a Review" button. You can rate your experience on a 5-star scale and share details about your visit, the quality of care, office environment, and staff professionalism. All reviews are moderated to ensure authenticity and helpfulness for other patients.',
    },
    {
      category: 'users',
      question: 'Are the reviews on Revulist verified and authentic?',
      answer: 'Yes, we take review authenticity seriously. All reviews go through a moderation process to verify they are from actual patients and meet our community guidelines. We use verification methods to prevent fake reviews and ensure the information helps patients make informed healthcare decisions. Providers can respond to reviews but cannot remove legitimate patient feedback.',
    },
    {
      category: 'users',
      question: 'Can I book appointments directly through Revulist?',
      answer: 'Currently, Revulist provides contact information and links to provider websites where you can book appointments. Many providers list their phone numbers, email addresses, and website booking portals on their profiles. We are working on integrating direct appointment booking features in future updates.',
    },
    {
      category: 'pricing',
      question: 'What are the pricing plans for healthcare providers?',
      answer: 'Revulist offers flexible pricing plans for healthcare providers: Free Basic Listing (includes essential practice information and patient reviews), Premium Plan ($29/month - enhanced visibility, unlimited photos, priority placement in search results), and Professional Plan ($79/month - all Premium features plus Revulist Ads, advanced analytics, and dedicated support). Visit our Pricing page for detailed plan comparisons.',
    },
    {
      category: 'pricing',
      question: 'What is included in the free listing?',
      answer: 'The free basic listing includes: practice name and specialty, contact information and location, basic credentials and services, patient reviews and ratings, office hours, and insurance information. This is perfect for individual practitioners and small practices looking to establish an online presence in the North Brunswick healthcare community.',
    },
    {
      category: 'pricing',
      question: 'What are Revulist Ads and how do they work?',
      answer: 'Revulist Ads is our premium advertising solution available with the Professional Plan. Your practice will appear at the top of relevant search results, get featured placement on category pages, and receive highlighted badges on your listing. Ads are targeted based on specialty, location, and user search behavior to connect you with patients actively seeking your services.',
    },
    {
      category: 'pricing',
      question: 'Can I upgrade or downgrade my plan at any time?',
      answer: 'Yes, you can upgrade or downgrade your subscription plan at any time from your My Account dashboard. Upgrades take effect immediately, while downgrades will apply at the start of your next billing cycle. There are no long-term contracts or cancellation fees - you have complete flexibility to choose the plan that best fits your practice needs.',
    },
    {
      category: 'technical',
      question: 'How do I update my practice information?',
      answer: 'Log in to your provider account and navigate to My Account. From there, you can edit your profile information, update office hours, add new services, upload photos, and manage all aspects of your listing. Changes are typically reflected on your public profile within a few minutes of saving.',
    },
    {
      category: 'technical',
      question: 'I forgot my password. How can I reset it?',
      answer: 'Click on the "Sign In" button in the navigation menu, then select "Forgot Password" on the login page. Enter your registered email address and we will send you a password reset link. Follow the instructions in the email to create a new password and regain access to your account.',
    },
    {
      category: 'technical',
      question: 'How do I delete my account?',
      answer: 'To delete your account, log in and go to My Account > Settings. Scroll down to the Account Management section and click "Delete Account". Please note that deleting your account will permanently remove your listing, reviews, and all associated data. This action cannot be undone, so please ensure you want to proceed before confirming.',
    },
    {
      category: 'technical',
      question: 'Who can I contact for additional support?',
      answer: 'Our support team is here to help! You can reach us through the Contact page, email us at support@revulist.com, or call our office during business hours. We typically respond to inquiries within 24 hours on business days. For urgent technical issues, please mention "Urgent" in your subject line.',
    },
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-teal-100 max-w-3xl mx-auto mb-8">
            Find answers to common questions about Revulist, our healthcare provider directory platform, and how to get the most out of our services in North Brunswick, New Jersey.
          </p>
          
          {/* Search Box */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for answers..."
                className="w-full px-6 py-4 pr-12 rounded-full text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <button className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-teal-600 rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors cursor-pointer">
                <i className="ri-search-line text-white text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === category.id
                    ? 'bg-teal-600 text-white shadow-md'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ List */}
      <section className="flex-1 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-5 flex items-start justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 pr-4">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {faq.question}
                    </h4>
                    <span className="inline-block px-3 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                      {categories.find(c => c.id === faq.category)?.name}
                    </span>
                  </div>
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center">
                    <i
                      className={`ri-arrow-down-s-line text-2xl text-gray-400 transition-transform ${
                        activeIndex === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </div>
                </button>
                
                {activeIndex === index && (
                  <div className="px-6 pb-6 pt-2">
                    <div className="pl-0 border-l-4 border-teal-500 pl-4">
                      <p className="text-gray-700 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFAQs.length === 0 && (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-question-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No questions found
              </h3>
              <p className="text-gray-600">
                Try selecting a different category or search for specific topics.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center mx-auto mb-6">
            <i className="ri-customer-service-2-line text-3xl"></i>
          </div>
          <h2 className="text-3xl font-bold mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Can't find the answer you're looking for? Our support team is here to help you with any questions about Revulist healthcare provider listings and services.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="px-8 py-4 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
            >
              <i className="ri-mail-line mr-2"></i>
              Contact Support
            </a>
            <a
              href="/vendors"
              className="px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer inline-flex items-center justify-center"
            >
              <i className="ri-search-line mr-2"></i>
              Browse Providers
            </a>
          </div>
          
          {/* Contact Info */}
          <div className="mt-12 pt-8 border-t border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-mail-line text-2xl text-teal-400"></i>
                </div>
                <h4 className="font-semibold mb-1">Email Us</h4>
                <p className="text-gray-400 text-sm">support@revulist.com</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-phone-line text-2xl text-teal-400"></i>
                </div>
                <h4 className="font-semibold mb-1">Call Us</h4>
                <p className="text-gray-400 text-sm">(732) 555-0123</p>
              </div>
              <div>
                <div className="w-12 h-12 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-400"></i>
                </div>
                <h4 className="font-semibold mb-1">Business Hours</h4>
                <p className="text-gray-400 text-sm">Mon-Fri: 9AM - 6PM EST</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
